/**
 * TBB — Boost auction preview card (session 28).
 *
 * Why: Auction Plus's `<auction-info>` widget is restricted to PDPs
 * (the merchant intentionally disables collection bidding), so the
 * native widget cannot hydrate inside Boost SD product cards. We
 * also can't depend on Boost SD passing metafields to card markup
 * (it doesn't). Instead we render a small read-only "auction
 * preview" inside each card on auction surfaces.
 *
 * Data source: `window.auctionPlusSetup.auctions` — populated by
 * Auction Plus on collection/product pages with the full
 * active-auction registry (handle, start_price, high_bid,
 * starts_at, ends_at, status).
 *
 * Fallback: On pages where the registry isn't populated (homepage,
 * search results, vendor pages), we fetch the auction collection
 * page in the background and extract auction data from the
 * server-rendered `auctionApp.updateAuctions([…])` calls.
 * One lightweight fetch, no API key needed.
 *
 * Behavior on a matched card:
 *   - Replaces `.boost-sd__product-price` with a preview block
 *     showing the relevant price label + amount + countdown.
 *   - Click target stays the existing card link → PDP, where the
 *     real bid widget lives.
 *
 * Resilience:
 *   - Idempotent: re-runs on filter/sort/paginate via
 *     MutationObserver without duplicating slots.
 *   - Countdown ticks once per second from a single shared timer.
 *   - If auction data can't be resolved after fallback fetch,
 *     gives up silently — never blocks normal price rendering.
 */
(function () {
  'use strict';

  var SLOT_CLASS = 'tbb-boost-auction-slot';
  var SLOT_SELECTOR = '.' + SLOT_CLASS;
  var CARD_SELECTOR = '.boost-sd__product-item';
  var PRICE_SELECTOR = '.boost-sd__product-price';

  // Collection handle(s) that contain auction products.
  // Fetched as fallback when auctionPlusSetup.auctions is missing.
  var AUCTION_COLLECTION_HANDLES = [
    'nostalgia-auction'
  ];

  var auctionsByHandle = null; // built once when registry available
  var liveSlots = []; // {el, auction, targetTime, isStarted}
  var fetchAttempted = false; // only try the fallback once

  /* ── Index builder ──────────────────────────────────────────── */

  function buildIndex() {
    if (auctionsByHandle) return auctionsByHandle;
    var setup = window.auctionPlusSetup;
    if (!setup || !Array.isArray(setup.auctions)) return null;
    var idx = Object.create(null);
    for (var i = 0; i < setup.auctions.length; i++) {
      var a = setup.auctions[i];
      if (a && a.partner_product_handle && !a.canceled_at) {
        // Skip ended auctions — let normal price render.
        if (a.status === 'ended' || a.status === 'closed') continue;
        idx[a.partner_product_handle] = a;
      }
    }
    auctionsByHandle = idx;
    return idx;
  }

  /* ── Fallback: fetch auction data from collection page HTML ── */

  /**
   * Extracts auction objects from a collection page's HTML.
   * Auction Plus renders multiple `auctionApp.updateAuctions([…])`
   * calls — one per auction item, each containing a single-element
   * array. We collect them all.
   */
  function extractAuctionsFromHTML(html) {
    var marker = 'updateAuctions(';
    var results = [];
    var searchFrom = 0;

    while (true) {
      var idx = html.indexOf(marker, searchFrom);
      if (idx === -1) break;

      var arrayStart = idx + marker.length; // should point to [
      if (html.charAt(arrayStart) !== '[') {
        searchFrom = arrayStart;
        continue;
      }

      // Bracket-match to find the closing ]
      var depth = 0;
      var inStr = false;
      var esc = false;
      var found = false;
      for (var i = arrayStart; i < html.length && i < arrayStart + 20000; i++) {
        var ch = html.charAt(i);
        if (esc) { esc = false; continue; }
        if (ch === '\\' && inStr) { esc = true; continue; }
        if (ch === '"') { inStr = !inStr; continue; }
        if (inStr) continue;
        if (ch === '[') depth++;
        else if (ch === ']') {
          depth--;
          if (depth === 0) {
            try {
              var arr = JSON.parse(html.substring(arrayStart, i + 1));
              results = results.concat(arr);
            } catch (e) { /* skip malformed entry */ }
            found = true;
            searchFrom = i + 1;
            break;
          }
        }
      }
      if (!found) break; // malformed — stop searching
    }

    return results.length ? results : null;
  }

  /**
   * Fetches auction collection page(s) and merges all auctions into
   * auctionPlusSetup.auctions, then triggers a full card scan.
   */
  function fetchAuctionRegistry(callback) {
    if (fetchAttempted) return;
    fetchAttempted = true;

    var allAuctions = [];
    var pending = AUCTION_COLLECTION_HANDLES.length;

    AUCTION_COLLECTION_HANDLES.forEach(function (handle) {
      fetch('/collections/' + handle, {
        credentials: 'same-origin',
        headers: { 'Accept': 'text/html' }
      })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var auctions = extractAuctionsFromHTML(html);
          if (auctions && auctions.length) {
            allAuctions = allAuctions.concat(auctions);
          }
        })
        .catch(function () { /* silent — don't break price rendering */ })
        .finally(function () {
          pending--;
          if (pending <= 0 && allAuctions.length) {
            // Inject into the global setup so buildIndex() works.
            if (!window.auctionPlusSetup) window.auctionPlusSetup = {};
            window.auctionPlusSetup.auctions = allAuctions;
            auctionsByHandle = null; // reset so buildIndex re-runs
            callback();
          }
        });
    });
  }

  /* ── Card helpers ───────────────────────────────────────────── */

  function getCardHandle(card) {
    var raw = card.getAttribute('data-product');
    if (!raw) return null;
    try { return JSON.parse(raw).handle || null; }
    catch (e) { return null; }
  }

  // Native phrasing: "Starts in 23 hours", "Starts in 5 minutes",
  // "Ends in 2 days", etc. Singular/plural; rounds DOWN to the
  // largest unit so it matches the native widget's text.
  function fmtRelative(ms) {
    if (ms <= 0) return 'now';
    var s = Math.floor(ms / 1000);
    var m = Math.floor(s / 60);
    var h = Math.floor(m / 60);
    var d = Math.floor(h / 24);
    if (d >= 1) return d + (d === 1 ? ' day' : ' days');
    if (h >= 1) return h + (h === 1 ? ' hour' : ' hours');
    if (m >= 1) return m + (m === 1 ? ' minute' : ' minutes');
    return s + (s === 1 ? ' second' : ' seconds');
  }

  /* ── Slot builder (mirrors native <auction-info> inner HTML) ─ */

  function buildSlot(auction) {
    var now = Date.now();
    var startsAt = auction.starts_at ? new Date(auction.starts_at).getTime() : 0;
    var endsAt = auction.ends_at ? new Date(auction.ends_at).getTime() : 0;
    var isStarted = startsAt && startsAt <= now;
    var hasBid = auction.high_bid && auction.high_bid.display;
    var priceObj = hasBid ? auction.high_bid : auction.start_price;
    if (!priceObj || !priceObj.display) return null;

    var label = hasBid ? 'Current Bid' : 'Starting Bid';
    var timeLabel, targetTime, targetIso;
    if (isStarted && endsAt) {
      timeLabel = 'Ends in ';
      targetTime = endsAt;
      targetIso = auction.ends_at;
    } else if (startsAt) {
      timeLabel = 'Starts in ';
      targetTime = startsAt;
      targetIso = auction.starts_at;
    } else {
      timeLabel = '';
      targetTime = 0;
      targetIso = '';
    }

    var wrap = document.createElement('div');
    wrap.className = SLOT_CLASS;
    wrap.setAttribute('data-handle', auction.partner_product_handle);
    // NOTE: do NOT wrap children in a literal <auction-info> — it's
    // a Vue custom element that wipes its slot the moment it's
    // connected to the DOM. We render the same INNER markup the
    // native widget produces and rely on global Tailwind utilities
    // for styling.
    wrap.innerHTML =
      '<div class="auction auction-container">' +
        '<div class="my-2 flex justify-between">' +
          '<div class="flex flex-col text-sm gap-1">' +
            '<span class="text-sm uppercase text-gray-500">' + label + '</span>' +
            '<strong>' +
              '<span class="text-lg">' + priceObj.display + '</span>' +
            '</strong>' +
          '</div>' +
        '</div>' +
        (timeLabel
          ? '<div class="flex text-sm font-bold my-2">' +
              '<time datetime="' + targetIso + '">' +
                '<span><span class="mr-1 inline-block h-2 w-2 rounded-full bg-gray-300"></span></span>' +
                '<span class="' + SLOT_CLASS + '__phrase">' + timeLabel + fmtRelative(targetTime - now) + '</span>' +
              '</time>' +
            '</div>'
          : '') +
      '</div>';

    return {
      el: wrap,
      auction: auction,
      targetTime: targetTime,
      timeLabel: timeLabel,
      isStarted: isStarted
    };
  }

  /* ── Card processing ────────────────────────────────────────── */

  function processCard(card) {
    if (!card || card.querySelector(SLOT_SELECTOR)) return;
    var idx = buildIndex();
    if (!idx) return;
    var handle = getCardHandle(card);
    if (!handle || !idx[handle]) return;
    var price = card.querySelector(PRICE_SELECTOR);
    if (!price) return;

    var slot = buildSlot(idx[handle]);
    if (!slot) return;

    price.replaceWith(slot.el);
    liveSlots.push(slot);
  }

  function scanAll(root) {
    var cards = (root || document).querySelectorAll(CARD_SELECTOR);
    for (var i = 0; i < cards.length; i++) processCard(cards[i]);
  }

  /* ── Countdown ticker ───────────────────────────────────────── */

  function tick() {
    var now = Date.now();
    var keep = [];
    for (var i = 0; i < liveSlots.length; i++) {
      var s = liveSlots[i];
      if (!s.el.isConnected) continue; // dropped from DOM by Boost re-render
      var phraseEl = s.el.querySelector('.' + SLOT_CLASS + '__phrase');
      if (!phraseEl) { keep.push(s); continue; }

      // Auction transitioned from scheduled → active mid-session.
      if (!s.isStarted) {
        var startsAt = s.auction.starts_at ? new Date(s.auction.starts_at).getTime() : 0;
        if (startsAt && startsAt <= now) {
          s.isStarted = true;
          s.timeLabel = 'Ends in ';
          s.targetTime = s.auction.ends_at ? new Date(s.auction.ends_at).getTime() : 0;
        }
      }
      phraseEl.textContent = s.timeLabel + fmtRelative(s.targetTime - now);
      keep.push(s);
    }
    liveSlots = keep;
  }
  setInterval(tick, 1000);

  /* ── MutationObserver for Boost re-renders ───────────────────── */

  var mo = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if (n.matches && n.matches(CARD_SELECTOR)) processCard(n);
        else if (n.querySelectorAll) {
          var inner = n.querySelectorAll(CARD_SELECTOR);
          for (var k = 0; k < inner.length; k++) processCard(inner[k]);
        }
      }
    }
  });

  /* ── Bootstrap ──────────────────────────────────────────────── */

  function start() {
    var setup = window.auctionPlusSetup;
    var hasRegistry = setup && Array.isArray(setup.auctions) && setup.auctions.length > 0;

    if (hasRegistry) {
      // Collection/product page — data already present.
      scanAll(document);
    } else {
      // Homepage, search, vendor page, etc. — fetch the data.
      fetchAuctionRegistry(function () {
        scanAll(document);
      });
    }

    // Always observe for Boost filter/sort/paginate re-renders.
    mo.observe(document.body, { childList: true, subtree: true });
  }

  // Wait for DOM + auctionPlusSetup object (not necessarily .auctions).
  function whenReady(fn) {
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      if (window.auctionPlusSetup) {
        clearInterval(iv);
        fn();
      } else if (tries > 40) {
        clearInterval(iv); // give up after ~20s
      }
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { whenReady(start); });
  } else {
    whenReady(start);
  }
})();
